import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TransactionInvoice, Currency } from '@hero/schema';

import { useInvoiceDrawerStore } from '@hero/hooks-shared';
import { InvoiceIcon } from '@hero/web-icons';
import { useFeature } from '@hero/web-context';
import { Card } from '../../../app/Card/Card';
import { RefDisplay } from '../../../app/Ref/RefDisplay';
import { PriceBadge } from '../../../app/Price/PriceBadge';
import { Price } from '../../../app/Price';

export interface TransactionInvoiceCardProps {
  invoice: TransactionInvoice;
  currency: Currency;
  className?: string;
}

export const TransactionInvoiceCard: React.FC<TransactionInvoiceCardProps> = ({
  invoice,
  currency,
  className,
}) => {
  const { t } = useTranslation(['m-payment']);
  const openInvoiceDrawer = useInvoiceDrawerStore(s => s.open);
  const invoiceModule = useFeature('invoice_module');

  const invoiceId = invoice._id?.toString();

  const handleOpenInvoice = invoiceId ? () => openInvoiceDrawer(invoiceId) : undefined;

  return (
    <Card onClick={invoiceModule.isEnabled ? handleOpenInvoice : undefined} className={className}>
      {/* Row 1: Invoice ref + amount received */}
      <div className="flex items-center justify-between gap-3">
        <RefDisplay
          refMain={invoice.ref?.main}
          refShort={invoice.ref?.short}
          icon={<InvoiceIcon className="size-3.5 text-gray-400 flex-shrink-0" />}
        />

        <PriceBadge price={invoice.amount} currency={currency} color="blue" />
      </div>

      {/* Row 2: Details */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
        <span>
          {t('m-payment:transaction.drawer.invoices.paid_change')}:{' '}
          <span className="font-medium text-gray-700">
            <Price amount={invoice.paidAmountChange} currency={currency} />
          </span>
        </span>
        <span className="text-gray-300">·</span>
        <span>
          {t('m-payment:transaction.drawer.invoices.invoice_total')}:{' '}
          <span className="font-medium text-gray-700">
            <Price amount={invoice.invoiceCurrentTotal} currency={currency} />
          </span>
        </span>
      </div>
    </Card>
  );
};
